// Fire-Alarm Submittal Package generator. Produces a PE-ready WORD (.doc) document
// the licensed engineer can edit/adjust/stamp — not a stamped design itself. Built
// as Word-compatible HTML (Office namespaces), so it opens and edits in Word with
// no external library and no dynamic import. Branded teal/gold, never navy.

import type { DesignResult, DesignInput } from './faDesigner';
import { fmtMoney } from './faDesigner';

const esc = (s: unknown): string => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function sprinklerLabel(s: DesignInput['sprinklerType']): string {
  return s === '13' ? 'NFPA 13 (full)' : s === '13R' ? 'NFPA 13R (residential)' : s === '13D' ? 'NFPA 13D' : 'None';
}

const h2 = (t: string) => `<h2>${esc(t)}</h2>`;
const para = (t: string, muted = false) => `<p${muted ? ' class="mut"' : ''}>${esc(t)}</p>`;
const pageBreak = '<br style="page-break-before:always">';

function kvTable(rows: [string, string][]): string {
  return `<table class="kv">${rows.map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`).join('')}</table>`;
}
function bullets(items: string[]): string {
  return `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>`;
}
// A cell "reads numeric" (money / counts / measurements) → right-align that column.
const isNumericCell = (v: string): boolean =>
  /^[$≥≤~]?\s?[-+]?\d[\d,.\s]*(\s*(mA|Ah|A|V|ft|cd|hrs?|hr|%|in|RU|pts?|deg|°))?\.?$/i.test((v ?? '').trim());

function dataTable(headers: string[], rows: string[][]): string {
  // Decide alignment per COLUMN: right only if most of its cells are numeric.
  const numCol = headers.map((_, i) => {
    const vals = rows.map((r) => (r[i] ?? '').trim()).filter(Boolean);
    return vals.length > 0 && vals.filter(isNumericCell).length / vals.length > 0.6;
  });
  const cls = (i: number) => (numCol[i] ? ' class="num"' : '');
  const head = `<thead><tr>${headers.map((h, i) => `<th${cls(i)}>${esc(h)}</th>`).join('')}</tr></thead>`;
  const tbody = `<tbody>${rows.map((r, ri) => `<tr${ri % 2 ? ' class="alt"' : ''}>${r.map((c, i) => `<td${cls(i)}>${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
  return `<table class="data">${head}${tbody}</table>`;
}

function coverSheet(r: DesignResult): string {
  return `
    <div class="caution">
      <div class="caution-h">FOR PE / NICET-III REVIEW &amp; STAMP</div>
      <p>This package is a budgetary submittal-ready basis-of-design prepared by 3D Technology Services, Inc. It is NOT a stamped engineering document. The licensed Fire Protection Engineer or NICET Level III designer of record must verify all device counts/spacing, voltage drop, battery sizing and AHJ amendments before applying their stamp.</p>
    </div>
    ${h2('1. Project & Administrative')}
    ${kvTable([
      ['Project Name', r.input.projectName || '__________________________'],
      ['Project Address / AHJ', r.input.cityAhj || '__________________________'],
      ['Owner', '__________________________'],
      ['Installing Contractor', '3D Technology Services, Inc.  ·  C-7 / C-10 License #: __________  ·  3dtsi.com'],
      ['Engineer of Record (PE / NICET-III)', '__________________________  ·  License #: __________  ·  State: ____'],
      ['Occupancy Classification', r.input.occupancy === 'r2_condos' ? 'R-2 (Residential — condominiums)' : 'R-2 (Residential — apartments)'],
      ['Construction Type (per architect)', '____  (e.g. V-A / III-A / I-A)'],
      ['Dwelling Units', String(r.input.units)],
      ['Stories Above Grade', String(r.input.stories)],
      ['Building Height (ft)', String(r.input.heightFt ?? '—')],
      ['Gross Floor Area (sf)', String(r.input.grossSf ?? '—')],
      ['Sprinkler System', sprinklerLabel(r.input.sprinklerType)],
      ['High-Rise (CBC Sect.202)', r.flags.isHighRise ? 'YES — >=75 ft' : 'No'],
      ['Voice/EVACS Required', r.flags.requiresVoiceEvac ? 'YES' : 'No'],
    ])}
    ${h2('Budgetary ROM — including the stamped engineering package')}
    ${kvTable([
      ['Material', fmtMoney(r.totals.material)],
      ['Installation labor', `${fmtMoney(r.totals.laborCost)} (${r.totals.hours} hrs @ $${r.laborRate}/hr${r.input.prevailingWage ? ' prevailing wage' : ''})`],
      ['Licensed PE-stamped design package', `${fmtMoney(r.totals.engineering)}  (full AHJ submittal — ~${r.totals.sheets} sheets + calcs + cut sheets, sealed by the engineer of record)`],
      ['AHJ permit allowance', fmtMoney(r.totals.permit)],
      ['Markup (22%)', fmtMoney(r.totals.markup)],
      ['Design-Build Contingency (10%)', fmtMoney(r.totals.contingency)],
      ['TOTAL (sell price)', fmtMoney(r.totals.total)],
      ['Per Dwelling Unit', fmtMoney(r.totals.perUnit)],
    ])}
    ${para('The price ABOVE includes the licensed, professionally engineered, permit-ready fire-alarm design package — the sealed drawings, calculations and cut sheets a Fire Protection Engineer / NICET-III stamps and submits to the AHJ — plus the AHJ permit allowance, programming, 100%-point commissioning, and year-1 UL central-station monitoring. This document is the design-build BASIS OF DESIGN and scope for that package; it is not itself the stamped set.', true)}`;
}

function basisOfDesign(r: DesignResult): string {
  const items: [string, string][] = [
    ['NFPA 72 (2022)', 'National Fire Alarm and Signaling Code — fundamentals (Sect.10), pathways (Sect.12), ITM (Sect.14), initiating devices (Sect.17), notification appliances (Sect.18), emergency control functions (Sect.21), protected premises (Sect.23), ECS/EVACS (Sect.24).'],
    ['CBC Sect.907', `Fire Alarm Systems — Sect.907.2.9 (R-2 corridor smoke, sprinklered exception ${r.input.sprinklerType !== 'none' ? 'APPLIED' : 'NOT applicable'}), Sect.907.2.13 (high-rise — ${r.flags.isHighRise ? 'APPLIED' : 'not applicable'}), Sect.907.5.2 (notification thresholds).`],
    ['CBC Sect.1009.8', `Area-of-Refuge two-way communication — ${(r.counts.aor ?? 0) > 0 ? `${r.counts.aor} stations` : 'NOT required (sprinklered exception applied)'}.`],
    ['CBC Sect.915', `Carbon monoxide detection — ${r.counts.co ?? 0} addressable CO detectors.`],
    ['CBC Sect.3008 + NFPA 72 Sect.21.4 + ASME A17.1', `Elevator recall + hoistway — ${r.counts.elevatorRecall ?? 0} interface kit(s).`],
    ['NFPA 13 / 20', `Sprinkler waterflow/tamper + fire pump supervision — ${r.counts.waterflow ?? 0} flow / ${r.counts.tamper ?? 0} tamper / ${r.counts.firePump ?? 0} pump monitor.`],
    ['NFPA 90A', `Duct smoke detection (>2000 CFM) — ${r.counts.duct ?? 0} duct kits.`],
    ['NFPA 70 Article 760', 'Fire-alarm circuit pathways (Class A/B/X), FPLP cable, separation from power.'],
    ['UL listings', 'UL 864 (FACP), 268/268A (smoke), 521 (heat), 464 (audible), 1971 (strobe), 1481 (NAC PSU), 2017 (CO), 2572 (MNS).'],
  ];
  return pageBreak + h2('Basis of Design — Code Compliance')
    + para('Design basis prepared per the codes/standards listed below as adopted by the AHJ noted on the cover.')
    + bullets(items.map(([c, t]) => `${c} — ${t}`));
}

function deviceSchedule(r: DesignResult): string {
  const rows: [string, number, string][] = [
    [r.panel || 'Addressable Fire Alarm Control Panel', 1, r.flags.isLargePanel ? 'Networked panel (>=80 units / >=5 stories)' : 'Small-building panel'],
    ['LCD Annunciator', 1, '24V SLC drop'],
    ['IP/Cellular Communicator', 1, 'NFPA 72 Sect.26 transmission'],
    ['Battery Sets (FACP + Voice + spare)', r.counts.batteries ?? 0, '24h standby + alarm — final size per Sect.10.6 calc'],
    ['Voice/EVACS Amplifier', r.counts.voiceAmp ?? 0, r.flags.requiresVoiceEvac ? 'CBC Sect.907.2.13.2 / NFPA 72 Sect.24' : '—'],
    ['NAC Boosters / Power Extenders', r.counts.nacBooster ?? 0, 'Voltage-drop & NAC-load driven'],
    ['Smoke — common area', r.counts.smokeCommon ?? 0, 'NFPA 72 Sect.17.7 spot spacing'],
    ['Smoke — corridor', r.counts.smokeCorridor ?? 0, r.input.sprinklerType !== 'none' ? 'CBC Sect.907.2.9 exception applied' : '30 ft o.c. corridor coverage'],
    ['Heat Detectors', r.counts.heat ?? 0, 'Mechanical / chase / shunt-trip'],
    ['Duct Smoke Detectors', r.counts.duct ?? 0, 'NFPA 90A — >2000 CFM AHUs'],
    ['CO Detectors', r.counts.co ?? 0, 'CBC Sect.915'],
    ['Manual Pull Stations', r.counts.pull ?? 0, 'CBC Sect.907.4.2, NFPA 72 Sect.17.14'],
    ['Horn/Strobe (wall, 75 cd, 520 Hz)', r.counts.horn_strobe ?? 0, 'Sleeping rooms low-frequency Sect.18.4.5.3'],
    ['Strobe Only (wall)', r.counts.strobe_only ?? 0, 'Visible-only spaces'],
    ['Speaker/Strobe (voice EVACS)', r.counts.speaker_strobe ?? 0, r.flags.requiresVoiceEvac ? 'Sect.24 EVACS' : '—'],
    ['Strobe Sync Modules', r.counts.sync ?? 0, 'Sect.18.5.5.5.2'],
    ['Waterflow Modules', r.counts.waterflow ?? 0, 'NFPA 13'],
    ['Tamper Modules', r.counts.tamper ?? 0, 'NFPA 13'],
    ['Fire Pump Supervisory', r.counts.firePump ?? 0, 'NFPA 20 / NFPA 72 Sect.17.16'],
    ['Relay Modules (HVAC/door)', r.counts.relay ?? 0, 'Sect.21 HVAC shutdown, door release'],
    ['Elevator Recall Kits', r.counts.elevatorRecall ?? 0, 'CBC Sect.3008 + NFPA 72 Sect.21.4 + ASME A17.1'],
    ['Area-of-Refuge Two-Way', r.counts.aor ?? 0, 'CBC Sect.1009.8 / Sect.907.5.2.3.4'],
    ['Firefighter Two-Way Phone Jacks', r.counts.ffComm ?? 0, r.flags.isHighRise ? 'CBC Sect.907.2.13.2' : '—'],
    ['Magnetic Door Holders', r.counts.doorHolder ?? 0, 'NFPA 72 Sect.21.5'],
    ['FPLR Wire (LF)', r.counts.wire_lf ?? 0, 'Class B/A pathways — verify against drawing takeoff'],
    ['Conduit EMT (LF)', r.counts.conduit_lf ?? 0, `${r.counts._conduitFracPct ?? 30}% conduit fraction for ${sprinklerLabel(r.input.sprinklerType)}`],
    ['CI Cable 2-hr Survivable (LF)', r.counts.ciCable_lf ?? 0, 'High-rise voice riser CBC Sect.907.6.4 / NFPA 72 Sect.12.4'],
  ];
  return pageBreak + h2('Device Schedule (computed)')
    + para('Device counts are deterministic from the project inputs and the code basis above. The PE/NICET-III shall verify spacing per NFPA 72 Sect.17.7 / Sect.18.5 layout calcs prior to stamp.')
    + dataTable(['Device', 'Qty', 'Code / Notes'], rows.map(([d, q, n]) => [d, q.toLocaleString(), n]));
}

function bomSection(r: DesignResult): string {
  return pageBreak + h2('Bill of Materials')
    + dataTable(['Description', 'Qty', 'Unit $', 'Ext $', 'Unit hrs', 'Ext hrs'],
      r.bom.map((b) => [b.description, b.quantity.toLocaleString(), fmtMoney(b.unit_material), fmtMoney(b.ext_material), b.unit_hours.toFixed(2), b.ext_hours.toFixed(1)]))
    + `<table class="total"><tr><td><b>TOTAL BUDGETARY ROM</b><br><span class="mut">Per dwelling unit: ${esc(fmtMoney(r.totals.perUnit))}</span></td><td class="big">${esc(fmtMoney(r.totals.total))}</td></tr></table>`;
}

function batteryCalc(r: DesignResult): string {
  const c = r.counts; const n = (x: number | undefined) => x ?? 0;
  // Representative published-style current draws (mA) [standby ea, alarm ea].
  // EOR confirms against the SELECTED equipment's spec sheets.
  // FACP control-equipment draw scales with panel class — a networked FACP (large
  // jobs) draws materially more than a small-building panel. (Was hard-coded 250/350.)
  const facpStby = r.flags.isLargePanel ? 350 : 180;
  const facpAlarm = r.flags.isLargePanel ? 500 : 250;
  const slc: [string, number, number, number][] = [
    ['SLC smoke detectors', n(c.smokeCommon) + n(c.smokeCorridor), 0.30, 0.50],
    ['SLC heat detectors', n(c.heat), 0.30, 0.50],
    ['Duct smoke detectors', n(c.duct), 0.50, 1.00],
    ['CO detectors', n(c.co), 0.30, 0.50],
    ['Manual pull stations', n(c.pull), 0.30, 0.50],
    ['Monitor / control / relay modules', n(c.waterflow) + n(c.tamper) + n(c.relay) + n(c.firePump) + n(c.elevatorRecall), 0.40, 0.50],
    [`FACP CPU + SLC driver + comm. (${r.flags.isLargePanel ? 'networked' : 'small-building'} panel)`, 1, facpStby, facpAlarm],
    ['LCD annunciator', 1, 40, 60],
    ['IP / cellular communicator', 1, 80, 100],
  ];
  const nac: [string, number, number, number][] = [
    ['Horn/strobes @ 75 cd (alarm)', n(c.horn_strobe), 0, 166],
    ['Strobes @ 75 cd (alarm)', n(c.strobe_only), 0, 119],
    ['Speaker/strobes — strobe element (alarm)', n(c.speaker_strobe), 0, 135],
  ];
  let stbyMA = 0, alarmMA = 0; const rows: string[][] = [];
  for (const [name, qty, se, ae] of [...slc, ...nac]) {
    if (!qty) continue;
    const s = qty * se, a = qty * ae; stbyMA += s; alarmMA += a;
    rows.push([name, String(qty), se.toFixed(2), s.toFixed(1), ae.toFixed(2), a.toFixed(1)]);
  }
  // Voice/EVACS amplifier — the DOMINANT alarm draw on voice jobs (audio power to the
  // speaker taps), previously OMITTED here, which under-sized voice/EVACS batteries.
  // Audio load ≈ 1 W per speaker tap (50 W floor); amplifier alarm current = audio W
  // ÷ 24 V ÷ 0.85 efficiency, plus ~250 mA/amp electronics. EOR confirms vs the amp.
  if (n(c.voiceAmp) > 0) {
    const amps = n(c.voiceAmp);
    const audioW = Math.max(50, n(c.speaker_strobe) * 1.0);
    const ampAlarmMA = Math.round((audioW / 24 / 0.85) * 1000 + 250 * amps);
    const ampStbyMA = 500 * amps;
    stbyMA += ampStbyMA; alarmMA += ampAlarmMA;
    rows.push([`Voice/EVACS amplifier — audio to speakers (${amps}× · ${audioW} W)`, String(amps),
      (ampStbyMA / amps).toFixed(0), ampStbyMA.toFixed(1), (ampAlarmMA / amps).toFixed(0), ampAlarmMA.toFixed(1)]);
  }
  const stbyA = stbyMA / 1000, alarmA = alarmMA / 1000;
  const alarmMin = r.flags.requiresVoiceEvac ? 15 : 5;
  const stbyAh = stbyA * 24, alarmAh = alarmA * (alarmMin / 60);
  const reqAh = (stbyAh + alarmAh) * 1.20;
  const battery = reqAh > 50 ? '2 × 75 Ah' : reqAh > 33 ? '2 × 55 Ah' : reqAh > 26 ? '2 × 33 Ah' : reqAh > 18 ? '2 × 26 Ah' : reqAh > 12 ? '2 × 18 Ah' : '2 × 12 Ah';
  return pageBreak + h2('8. Secondary-Power (Battery) Calculation')
    + para(`Per NFPA 72 Sect.10.6: 24 hours of standby PLUS ${alarmMin} minutes of alarm, with a 20% (×1.20) aging/derate factor. Currents are representative published draws — the EOR confirms against the approved equipment. The FACP control-equipment draw scales with panel class (networked vs small-building)${n(c.voiceAmp) > 0 ? ', and the voice/EVACS amplifier audio load IS included here — it is the dominant alarm draw on voice jobs' : ''}. Appliances on remote NAC power supplies are carried by those supplies' own batteries, sized by the same method.`)
    + dataTable(['Load', 'Qty', 'Standby mA ea', 'Standby mA', 'Alarm mA ea', 'Alarm mA'], rows)
    + dataTable(['Calculation', 'Value', 'Basis'], [
      ['Total standby current', `${stbyA.toFixed(3)} A`, `${stbyMA.toFixed(0)} mA summed`],
      ['Total alarm current', `${alarmA.toFixed(3)} A`, `${alarmMA.toFixed(0)} mA summed (standby + notification)`],
      ['Standby capacity', `${stbyAh.toFixed(2)} Ah`, '24 h × standby'],
      ['Alarm capacity', `${alarmAh.toFixed(2)} Ah`, `${alarmMin} min × alarm`],
      ['Required (×1.20 derate)', `${reqAh.toFixed(2)} Ah`, 'NFPA 72 Sect.10.6'],
      ['Battery (basis of design)', `${battery} sealed lead-acid`, 'EOR finalizes from published draws'],
    ]);
}

function voltageDrop(r: DesignResult): string {
  const c = r.counts; const n = (x: number | undefined) => x ?? 0;
  const naTotal = n(c.horn_strobe) + n(c.strobe_only) + n(c.speaker_strobe);
  // Available NAC circuits: ~4 per booster + 2 on the FACP.
  const circuits = Math.max(1, n(c.nacBooster) * 4 + 2);
  const perCkt = Math.ceil(naTotal / circuits);
  const iPerCkt = perCkt * 0.166;                 // A, avg 75 cd horn/strobe
  const Vsupply = 20.4;                            // V, regulated output at low battery
  const Vmin = 16.0;                               // V, appliance minimum
  const ohmPerFt14 = 2.525 / 1000;                 // 14 AWG copper Ω/ft
  const Lmax = Math.round((Vsupply - Vmin) / (2 * ohmPerFt14 * Math.max(0.05, iPerCkt))); // ft, max run @ this load
  const Lassumed = Math.min(Lmax, 400);            // representative design run
  const vdrop = iPerCkt * 2 * ohmPerFt14 * Lassumed;
  const veol = Vsupply - vdrop;
  return pageBreak + h2('9. Notification Voltage-Drop Calculation (per NAC)')
    + para('Each NAC must hold ≥ 16.0 V at the most-distant appliance with the supply at its low-battery regulated output (≈ 20.4 V). Worked below with 14 AWG copper and the worst-case load lumped at end-of-line (conservative). The EOR recomputes per the actual circuit length, gauge and appliance distribution on the stamped drawings.')
    + dataTable(['Parameter', 'Value', 'Basis'], [
      ['Total notification appliances', String(naTotal), 'From the equipment schedule'],
      ['NAC circuits available', String(circuits), `${n(c.nacBooster)} booster(s) × 4 + 2 FACP`],
      ['Appliances per circuit', String(perCkt), 'Even distribution (EOR balances)'],
      ['Current per circuit', `${iPerCkt.toFixed(2)} A`, '≈ 0.166 A per 75 cd horn/strobe'],
      ['Conductor', '14 AWG copper', `${(ohmPerFt14 * 1000).toFixed(3)} Ω/1000 ft`],
      ['Max run to hold 16.0 V', `${Lmax} ft`, 'Vavail ÷ (2 × R × I)'],
      ['Design run (worst case)', `${Lassumed} ft`, 'Representative — EOR uses actual'],
      ['Voltage drop', `${vdrop.toFixed(2)} V`, '2 × R × L × I'],
      ['End-of-line voltage', `${veol.toFixed(2)} V`, veol >= Vmin ? 'PASS (≥ 16.0 V)' : 'ADD BOOSTER / upsize wire'],
    ])
    + para(`Pathway materials: FPLR/FPLP ${r.counts.wire_lf ?? 0} LF; EMT where required ${r.counts.conduit_lf ?? 0} LF (${r.counts._conduitFracPct ?? 30}% for ${sprinklerLabel(r.input.sprinklerType)})${(r.counts.ciCable_lf ?? 0) > 0 ? `; 2-hr CI survivable cable ${r.counts.ciCable_lf} LF (CBC Sect.907.6.4)` : ''}.`, true);
}

function nacCircuitSchedule(r: DesignResult): string {
  const c = r.counts; const n = (x?: number) => x ?? 0;
  const grossSf = +(r.input.grossSf || r.input.units * 950);
  const heightFt = +(r.input.heightFt || r.input.stories * 11);

  // Per-appliance alarm current (A) — same basis as the worked voltage-drop example.
  const appls: { name: string; qty: number; mA: number }[] = [
    { name: 'Horn/strobe 75 cd', qty: n(c.horn_strobe), mA: 166 },
    { name: 'Strobe 75 cd', qty: n(c.strobe_only), mA: 119 },
    { name: 'Speaker/strobe', qty: n(c.speaker_strobe), mA: 135 },
  ].filter((a) => a.qty > 0);
  const totalAppls = appls.reduce((s, a) => s + a.qty, 0);
  const totalMA = appls.reduce((s, a) => s + a.qty * a.mA, 0);
  const avgMA = totalAppls ? totalMA / totalAppls : 0;

  // Real circuits available: 2 on the FACP + 4 per booster / power extender.
  const labels: string[] = ['FACP NAC-1', 'FACP NAC-2'];
  for (let b = 1; b <= n(c.nacBooster); b++) for (let k = 1; k <= 4; k++) labels.push(`Booster B${b} NAC-${k}`);
  const circuits = labels.length;
  const base = Math.floor(totalAppls / circuits);
  const extra = totalAppls % circuits;

  // Worst-case home-run length for this building (EOR uses the actual drawing run).
  const designRun = Math.min(500, Math.max(150, Math.round(Math.sqrt(grossSf) * 1.3 + heightFt * 0.5)));
  const gauges: { awg: string; ohmPerFt: number }[] = [
    { awg: '18 AWG', ohmPerFt: 6.385 / 1000 },
    { awg: '16 AWG', ohmPerFt: 4.016 / 1000 },
    { awg: '14 AWG', ohmPerFt: 2.525 / 1000 },
    { awg: '12 AWG', ohmPerFt: 1.588 / 1000 },
  ];
  const Vsupply = 20.4, Vmin = 16.0;

  if (totalAppls === 0) {
    return pageBreak + h2('9a. NAC Circuit Schedule (balanced per real circuit)')
      + para('No notification appliances in this scope — NAC circuit schedule not applicable.');
  }

  const rows: string[][] = [];
  let worst = Infinity;
  for (let i = 0; i < circuits; i++) {
    const appl = base + (i < extra ? 1 : 0);
    if (appl === 0) { rows.push([labels[i]!, '0 (spare)', '—', '—', '—', '—']); continue; }
    const I = (appl * avgMA) / 1000;                          // A
    // Minimum gauge that still holds >=16.0 V over the design run at this current.
    let pick = gauges[gauges.length - 1]!;
    for (const g of gauges) {
      if ((Vsupply - Vmin) / (2 * g.ohmPerFt * Math.max(0.01, I)) >= designRun) { pick = g; break; }
    }
    const maxRun = Math.floor((Vsupply - Vmin) / (2 * pick.ohmPerFt * Math.max(0.01, I)));
    const eol = Vsupply - 2 * pick.ohmPerFt * I * designRun;
    worst = Math.min(worst, eol);
    const gaugeLabel = maxRun >= designRun ? pick.awg : `${pick.awg} ⚠ split/booster`;
    rows.push([labels[i]!, String(appl), I.toFixed(2), gaugeLabel, String(maxRun), `${eol.toFixed(1)} V`]);
  }

  const okMsg = !Number.isFinite(worst) ? ''
    : worst >= Vmin
      ? `All circuits hold ≥ 16.0 V at end-of-line (worst case ${worst.toFixed(1)} V).`
      : `One or more circuits fall below 16.0 V at ${designRun} ft — add a booster, shorten the run, or upsize the conductor.`;
  return pageBreak + h2('9a. NAC Circuit Schedule (balanced per real circuit)')
    + para(`The ${totalAppls} notification appliance(s) are balanced across the ${circuits} real NAC circuit(s) — 2 on the FACP plus 4 per power-extender/booster. For each circuit the minimum copper conductor gauge is chosen to hold ≥ 16.0 V at the most-distant appliance over a worst-case ${designRun} ft home-run, with the supply at its low-battery regulated output (≈ 20.4 V). The EOR re-balances against the actual appliance locations and run lengths on the stamped drawings.`)
    + dataTable(['NAC circuit', 'Appliances', 'Alarm A', 'Min wire gauge', 'Max run (ft)', `EOL @ ${designRun} ft`], rows)
    + para(`Average appliance draw ≈ ${avgMA.toFixed(0)} mA; load each NAC to ≤ 80% of its source rating per NFPA 72 Sect.23. ${okMsg}`, true);
}

function slcLoopSchedule(r: DesignResult): string {
  const c = r.counts; const n = (x?: number) => x ?? 0;
  // Addressable SLC points split into the two Notifier-style address spaces:
  // detectors and modules (each up to ~159 per loop, panel-dependent).
  const detectors: { name: string; qty: number }[] = [
    { name: 'Smoke — common area', qty: n(c.smokeCommon) },
    { name: 'Smoke — corridor', qty: n(c.smokeCorridor) },
    { name: 'Heat detectors', qty: n(c.heat) },
    { name: 'Duct smoke detectors', qty: n(c.duct) },
    { name: 'CO detectors', qty: n(c.co) },
  ].filter((d) => d.qty > 0);
  const modules: { name: string; qty: number }[] = [
    { name: 'Pull stations', qty: n(c.pull) },
    { name: 'Waterflow modules', qty: n(c.waterflow) },
    { name: 'Tamper modules', qty: n(c.tamper) },
    { name: 'Relay / control modules', qty: n(c.relay) },
    { name: 'Fire pump supervisory', qty: n(c.firePump) },
    { name: 'Elevator recall interface', qty: n(c.elevatorRecall) },
  ].filter((m) => m.qty > 0);

  const CAP = 159;                                  // points per address space per loop
  const detPts = detectors.reduce((s, d) => s + d.qty, 0);
  const modPts = modules.reduce((s, m) => s + m.qty, 0);
  if (detPts + modPts === 0) {
    return pageBreak + h2('10a. SLC Loop Loading & Starting Device-Address Schedule')
      + para('No addressable SLC devices in this scope.');
  }
  const loops = Math.max(1, Math.ceil(detPts / CAP), Math.ceil(modPts / CAP));

  const loadRows: string[][] = [];
  for (let L = 1; L <= loops; L++) {
    const d = Math.max(0, Math.min(CAP, detPts - (L - 1) * CAP));
    const m = Math.max(0, Math.min(CAP, modPts - (L - 1) * CAP));
    const spare = Math.round((1 - Math.max(d, m) / CAP) * 100);
    loadRows.push([`SLC Loop ${L}`, `${d} / ${CAP}`, `${m} / ${CAP}`, `${spare}%`]);
  }

  // Starting address schedule — contiguous ranges per device type across loops.
  const alloc = (types: { name: string; qty: number }[], prefix: string): string[][] => {
    const out: string[][] = []; let loop = 1, addr = 1;
    for (const t of types) {
      let rem = t.qty;
      while (rem > 0) {
        const take = Math.min(rem, CAP - (addr - 1));
        out.push([`L${loop}`, t.name, String(take), `${prefix}${addr}–${prefix}${addr + take - 1}`]);
        addr += take; rem -= take;
        if (addr > CAP) { loop++; addr = 1; }
      }
    }
    return out;
  };

  return pageBreak + h2('10a. SLC Loop Loading & Starting Device-Address Schedule')
    + para(`${detPts + modPts} addressable point(s) — ${detPts} detector(s) + ${modPts} module(s) — across ${loops} signaling line circuit (SLC) loop(s) at ~${CAP} points per address space per loop (panel-dependent). Spare capacity per loop is shown for future devices (target ≥ 20%). The address ranges below are a STARTING assignment the EOR / programmer finalizes against the stamped floor plans and the selected panel's loop architecture.`)
    + dataTable(['SLC loop', 'Detector pts', 'Module pts', 'Spare'], loadRows)
    + para('Detector address ranges (D-addresses):')
    + dataTable(['Loop', 'Device type', 'Qty', 'Address range'], alloc(detectors, 'D'))
    + para('Module address ranges (M-addresses):')
    + dataTable(['Loop', 'Device type', 'Qty', 'Address range'], alloc(modules, 'M'));
}

function scopeLetter(r: DesignResult): string {
  const included = [
    'Stamped engineering by a licensed Fire Protection Engineer / NICET Level III designer of record',
    'Riser diagram, battery + voltage-drop calculations, spec compliance matrix',
    'AHJ permit submittal and revisions',
    'All devices listed on the Device Schedule and Bill of Materials',
    'FPLP/FPLR cable, EMT raceway where required, ' + (r.flags.isHighRise ? '2-hour CI survivable cable for voice/EVACS risers, ' : '') + 'terminations',
    'Programming, 100% point-to-point pre-test, AHJ witnessed acceptance test',
    'Owner training (4 hours), as-built drawings, O&M manual',
    'First-year UL Central Station monitoring (renewal at owner option)',
  ];
  const excluded = [
    'Sprinkler piping/heads, fire pump, kitchen suppression, smoke management dampers',
    'Patching, painting, ceiling repair after device installation',
    'Removal/abatement of hazardous materials',
    'Power circuits to FACP and door holders (by Division 26)',
    'AHJ permit fees in excess of allowance (TBD by AHJ)',
    'Stock of attic stock devices beyond standard manufacturer allotment',
  ];
  return pageBreak + h2('Scope of Work — Letter')
    + para('3D Technology Services, Inc. proposes to design, furnish, install, program, test, and commission a complete addressable fire-alarm system for the project identified on the cover, on a design-build basis. The system shall be installed in accordance with NFPA 72 (2022), CBC Sect.907 (2022 California Building Code), CFC, NFPA 70 Article 760, and the AHJ adopted amendments.')
    + '<p><b>INCLUDED:</b></p>' + bullets(included)
    + '<p><b>EXCLUDED:</b></p>' + bullets(excluded);
}

function rfiChecklist(r: DesignResult): string {
  const items = [
    'Confirm AHJ adopted edition of NFPA 72 + local amendments — revise basis if not 2022.',
    'Verify dwelling-unit floor plan device coverage against NFPA 72 Sect.17.7 spacing tables (smoke) and Sect.17.8 (heat) — adjust counts where required.',
    'Run NAC voltage-drop calc per circuit at the worst-case appliance. Add boosters if any circuit drops below 16.0 V at end-of-line.',
    'Battery sizing — replace approximations in the Secondary Power worksheet with the published draw for the SELECTED FACP / amplifier / comm / annunciator + appliance currents; finalize Ah size with 20% derate.',
    'Strobe spacing — perform NFPA 72 Sect.18.5 visible-spacing calc room-by-room for common areas, corridors, sleeping rooms; adjust candela ratings and quantities to the stamped layout.',
    r.input.sprinklerType !== 'none' ? 'Confirm CBC Sect.907.2.9 corridor smoke EXCEPTION applies (fully sprinklered). If partial sprinkler coverage, add corridor smokes at 30 ft o.c.' : 'Non-sprinklered: confirm corridor smoke 30 ft o.c. across all egress corridors.',
    r.flags.requiresVoiceEvac ? 'Voice/EVACS — verify intelligibility (STI-PA >= 0.45) per NFPA 72 Sect.18.4.10; size voice amplifier(s) for total speaker tap loads + 20% spare; confirm 15-min alarm secondary-power calc.' : 'Verify voice/EVACS is NOT required by the AHJ for this occupancy + occupant load — base-build horn/strobe is provisioned.',
    r.flags.isHighRise ? 'High-rise — coordinate with CBC Sect.403/Sect.907.2.13: emergency responder communication, FCC location, smoke control interface, 2-hour CI riser routing.' : 'Non-high-rise — confirm there is no AHJ-required mass-notification overlay (CBC Sect.907.2.18) for this occupancy.',
    'Elevator recall — coordinate with elevator contractor per NFPA 72 Sect.21.4 and ASME A17.1. Confirm shunt-trip requirements with sprinkler installer.',
    'CO detection — confirm fuel-fired equipment present (boilers, water heaters, generators) and parking-garage adjacency per CBC Sect.915.',
    'AHJ permit fee allowance ($4,500 placeholder) — replace with the AHJ posted fee schedule.',
    'Prevailing wage — confirm with the GC: ' + (r.input.prevailingWage ? 'CA Sound & Communication classification ASSUMED ($145/hr).' : 'Open-shop labor ASSUMED ($95/hr).'),
  ];
  return pageBreak + h2('PE Stamp Review — RFI / Action Checklist')
    + para('The licensed engineer of record shall confirm/adjust the following before applying their stamp:')
    + `<ol>${items.map((t) => `<li>${esc(t)}</li>`).join('')}</ol>`;
}

function stampBlock(): string {
  return pageBreak + h2("Engineer's Stamp & Signature")
    + para('The licensed Fire Protection Engineer (PE) or NICET Level III certified fire-alarm designer of record applies stamp and signature in the box below upon completion of review. This package becomes the basis-of-design for the stamped submittal.')
    + `<table class="stamp"><tr><td>
        <p class="mut"><b>PE / NICET-III STAMP</b></p>
        <p>Name (printed) &nbsp;_____________________________________________</p>
        <p>Title &nbsp;_________________________ &nbsp;License # _______________</p>
        <p>Signature &nbsp;_____________________________ &nbsp;Date &nbsp;______________</p>
        <p>NICET FA Level _______ (if applicable)</p>
        <p class="mut">Drawing set reviewed: T0.01 / T-FA / Riser / Battery Calc / Voltage-Drop Calc</p>
        <p class="mut">Reviewed against AHJ-adopted edition of NFPA 72 (_____) and CBC Sect.907 (_____)</p>
      </td></tr></table>`
    + para('By signing above, the engineer of record assumes professional responsibility for the design as finalized on the stamped drawings, in accordance with California Business & Professions Code Sect.6700-6799 and the AHJ adopted codes.', true);
}

function sheetIndex(r: DesignResult): string {
  const rows = [
    ['T0.01', 'Cover, Sheet Index, Codes & Scope'],
    ['T0.02', 'Code Analysis & Compliance Matrix'],
    ['T0.03', 'Device Legend & Abbreviations'],
    ['T1.0x', `Fire Alarm Floor Plans (per floor — ${r.input.stories} level${r.input.stories === 1 ? '' : 's'})`],
    ['T2.01', 'Fire Alarm Riser Diagram'],
    ['T3.01', 'Sequence of Operations Matrix'],
    ['T4.01', 'Equipment Schedule'],
    ['T5.01', 'Battery / Voltage-Drop / Power-Supply Calculations'],
    ['T5.02', 'Candela & Audibility Coverage Calculations'],
    ['T6.01', 'Interface & Emergency Control Function Details'],
    ['T7.01', 'Specifications (Division 28) & Product Cut Sheets'],
  ];
  return pageBreak + h2('Sheet Index')
    + para(`The stamped AHJ package will comprise approximately ${r.totals.sheets} sheets plus the calculation package and manufacturer cut sheets (typical commercial range 20–100).`)
    + dataTable(['Sheet', 'Title'], rows);
}

function codeAnalysis(r: DesignResult): string {
  const codes = [
    ['NFPA 72 (2022)', 'National Fire Alarm & Signaling Code'],
    ['NFPA 70 (NEC), Article 760', 'Fire-alarm circuit wiring methods & power'],
    ['NFPA 101', 'Life Safety Code (egress / occupancy)'],
    ['CBC / IBC Sect.907', 'Building Code — fire alarm systems'],
    ['California / Local Fire Code (CFC)', 'AHJ-adopted fire code + local amendments'],
    ['Energy Code (Title 24, if applicable)', 'Standby power / equipment efficiency'],
    ['ADA / CBC Ch.11 (Accessibility)', 'Visible notification & reach ranges'],
    ['Local AHJ Amendments', 'Confirm adopted edition + amendments with the AHJ'],
  ];
  const analysis: [string, string][] = [
    ['Occupancy type', r.input.occupancy === 'r2_condos' ? 'R-2 condominiums' : 'R-2 apartments'],
    ['Hazard classification', 'Light hazard (residential) — verify per CFC'],
    ['Required system type', r.flags.requiresVoiceEvac ? 'Addressable + Voice/EVACS (ECS)' : 'Addressable, building-wide'],
    ['Required monitoring', 'Yes — UL-listed supervising (central) station, NFPA 72 Sect.26'],
    ['Notification coverage', `Audible + visible throughout; sleeping-room low-frequency (520 Hz) per Sect.18.4.5; ${r.flags.requiresVoiceEvac ? 'intelligible voice per Sect.18.4.10' : 'horn/strobe'}`],
    ['Initiating-device coverage', `Corridor/common smoke (Sect.17.7), heat in mech/elec, duct (NFPA 90A), pull stations (Sect.17.14)`],
    ['Emergency communication', r.flags.requiresVoiceEvac ? 'EVACS voice required (CBC Sect.907.2.13.2 / NFPA 72 Sect.24)' : 'Not required for this occupancy/height — confirm with AHJ'],
    ['Elevator recall', `${(r.counts.elevatorRecall ?? 0) > 0 ? `${r.counts.elevatorRecall} kit(s)` : 'As required'} — CBC Sect.3008 / NFPA 72 Sect.21.4 / ASME A17.1`],
    ['Smoke control', r.flags.isHighRise ? 'Coordinate high-rise smoke-control interface (CBC Sect.909)' : 'Not required (verify with AHJ)'],
    ['Door release / holders', `${(r.counts.doorHolder ?? 0)} magnetic holder(s) released on alarm — NFPA 72 Sect.21.5`],
  ];
  return pageBreak + h2('2. Code Analysis')
    + para('Applicable codes & standards (as adopted by the AHJ on the cover):')
    + dataTable(['Code / Standard', 'Applies to'], codes)
    + para('Code analysis:')
    + kvTable(analysis);
}

function drawingDeliverables(r: DesignResult): string {
  return pageBreak + h2('3. Floor Plans (deliverable — by Engineer of Record)')
    + para(`The stamped set includes a fire-alarm plan for EACH floor (${r.input.stories} level${r.input.stories === 1 ? '' : 's'}) on the architectural background, showing: room names & numbers, scale, north arrow, every device with its address and circuit designation. Devices shown: smoke / heat / beam / duct detectors, pull stations, waterflow & tamper switches, control & monitor modules, horns, strobes, horn/strobes, speaker/strobes, firefighter phones, remote indicators, annunciators, the FACP, power supplies and network nodes.`)
    + para('Floor plans are CAD drawings produced and sealed by the engineer of record using the architectural backgrounds — they are part of the stamped package priced on the cover, not reproduced in this basis-of-design document.', true);
}

function riserDiagram(): string {
  return pageBreak + h2('4. Fire Alarm Riser Diagram (deliverable — by Engineer of Record)')
    + para('The riser diagram (sealed) will show: the FACP, remote power supplies, network connections, NAC circuits, SLC circuits, monitor modules, control modules, the supervising-station connection, and every interface — elevator, HVAC, generator, smoke-control and door-holder.');
}

function sequenceMatrix(r: DesignResult): string {
  const rows: string[][] = [
    ['Smoke detector alarm', 'General alarm — evacuation signal building-wide; notify supervising station'],
    ['Pull station alarm', 'General alarm — evacuation signal building-wide; notify supervising station'],
    ['Waterflow switch alarm', 'General alarm — evacuation signal; notify supervising station'],
    ['Duct smoke detector', 'Shut down associated HVAC unit (NFPA 90A); supervisory/alarm per AHJ'],
    ...(((r.counts.elevatorRecall ?? 0) > 0) ? [['Elevator-lobby / hoistway smoke or heat', 'Phase I elevator recall (primary/alternate); shunt trip where sprinklered']] : []),
    ['Sprinkler tamper switch', 'Supervisory signal; notify supervising station'],
    ['Low air / supervisory', 'Supervisory signal'],
    ['Magnetic door holders', 'Release on any general alarm'],
    ...(r.flags.requiresVoiceEvac ? [['Voice/EVACS activation', 'Automatic evacuation message; manual paging from FACC']] : []),
    ['Ground fault', 'Trouble signal'],
    ['NAC / SLC / power-supply failure', 'Trouble signal'],
    ['AC power loss', 'Trouble signal; battery standby'],
  ];
  return pageBreak + h2('5. Sequence of Operations Matrix')
    + para('Every programmed input → output. The engineer of record finalizes against the approved equipment and AHJ requirements.')
    + dataTable(['Event (input)', 'Response (output)'], rows);
}

function deviceLegend(): string {
  const rows = [
    ['S', 'Smoke detector (photoelectric, addressable)'], ['H', 'Heat detector (addressable)'],
    ['D', 'Duct smoke detector w/ remote test'], ['B', 'Beam detector (where applicable)'],
    ['CO', 'Carbon-monoxide detector'], ['F', 'Manual pull station'],
    ['HS', 'Horn/strobe'], ['ST', 'Strobe only'], ['SP', 'Speaker'], ['SS', 'Speaker/strobe'],
    ['MM', 'Monitor (input) module'], ['CM', 'Control (output) module'], ['RM', 'Relay module'],
    ['FFT', "Firefighter telephone jack"], ['RI', 'Remote indicator'],
    ['ANN', 'LCD annunciator'], ['PS', 'Remote power supply / NAC booster'],
    ['FACP', 'Fire alarm control panel'], ['NN', 'Network node'],
  ];
  return pageBreak + h2('6. Device Legend')
    + para('Every symbol used on the plans (final graphics per the EOR title block):')
    + dataTable(['Symbol', 'Device'], rows);
}

function equipmentSchedule(r: DesignResult): string {
  const m = (q: number | undefined) => (q ?? 0) > 0;
  // Representative "basis of design (or equal)" models — the EOR/installer confirms
  // exact current models + panel compatibility + listings in the approved submittal.
  const rows: string[][] = [
    ['Fire Alarm Control Panel', r.panel, '1', '60" to top', 'SLC/NAC', 'N/A'],
    ['LCD Annunciator', 'Notifier LCD-160 (or equal)', '1', '60"', 'SLC', 'by EOR'],
    ['IP/Cellular Communicator', 'per panel mfr (or equal)', '1', 'in FACP', '—', '—'],
    m(r.counts.voiceAmp) ? ['Voice/EVACS Amplifier', 'per panel mfr (or equal)', String(r.counts.voiceAmp), 'in rack', 'Audio', 'by EOR'] : [],
    m(r.counts.nacBooster) ? ['NAC Power Supply / Booster', 'System Sensor / Notifier (or equal)', String(r.counts.nacBooster), '60"', 'NAC', 'by EOR'] : [],
    m(r.counts.smokeCommon) ? ['Smoke Detector — common', 'Notifier FSP-951 (or equal)', String(r.counts.smokeCommon), 'ceiling', 'SLC', 'by EOR'] : [],
    m(r.counts.smokeCorridor) ? ['Smoke Detector — corridor', 'Notifier FSP-951 (or equal)', String(r.counts.smokeCorridor), 'ceiling', 'SLC', 'by EOR'] : [],
    m(r.counts.heat) ? ['Heat Detector', 'Notifier FST-951 (or equal)', String(r.counts.heat), 'ceiling', 'SLC', 'by EOR'] : [],
    m(r.counts.duct) ? ['Duct Smoke Detector', 'Notifier DNR(W) (or equal)', String(r.counts.duct), 'at AHU', 'SLC', 'by EOR'] : [],
    m(r.counts.co) ? ['CO Detector', 'Notifier FCO-851 (or equal)', String(r.counts.co), 'per CBC Sect.915', 'SLC', 'by EOR'] : [],
    m(r.counts.pull) ? ['Manual Pull Station', 'Notifier NBG-12LX (or equal)', String(r.counts.pull), '48"', 'SLC', 'by EOR'] : [],
    m(r.counts.horn_strobe) ? ['Horn/Strobe (wall)', 'System Sensor P2RL (or equal)', String(r.counts.horn_strobe), '80" / 6" below ceiling', 'NAC', 'by EOR'] : [],
    m(r.counts.strobe_only) ? ['Strobe (wall)', 'System Sensor SRL (or equal)', String(r.counts.strobe_only), '80"', 'NAC', 'by EOR'] : [],
    m(r.counts.speaker_strobe) ? ['Speaker/Strobe', 'System Sensor SPSRL (or equal)', String(r.counts.speaker_strobe), '80"', 'Audio/NAC', 'by EOR'] : [],
    m(r.counts.waterflow) ? ['Waterflow Monitor Module', 'Notifier FMM-1 (or equal)', String(r.counts.waterflow), 'at riser', 'SLC', 'by EOR'] : [],
    m(r.counts.tamper) ? ['Tamper Monitor Module', 'Notifier FMM-1 (or equal)', String(r.counts.tamper), 'at valve', 'SLC', 'by EOR'] : [],
    m(r.counts.relay) ? ['Control / Relay Module', 'Notifier FCM-1 / FRM-1 (or equal)', String(r.counts.relay), 'as required', 'SLC', 'by EOR'] : [],
    m(r.counts.elevatorRecall) ? ['Elevator Recall Interface', 'Notifier FRM-1 + relays (or equal)', String(r.counts.elevatorRecall), 'at controller', 'SLC', 'by EOR'] : [],
    m(r.counts.aor) ? ['Area-of-Refuge Two-Way Station', 'per ARC mfr (or equal)', String(r.counts.aor), '48"', 'ARC', 'by EOR'] : [],
    m(r.counts.doorHolder) ? ['Magnetic Door Holder', 'per mfr (or equal)', String(r.counts.doorHolder), 'wall/floor', 'NAC/aux', 'by EOR'] : [],
  ].filter((x) => x.length);
  return pageBreak + h2('7. Equipment Schedule')
    + para('Quantities are computed from the inputs; models are the basis of design ("or equal"). The engineer of record / installer confirms exact current models, panel compatibility and addresses in the approved submittal.')
    + dataTable(['Device', 'Mfr / Model (or equal)', 'Qty', 'Mounting', 'Circuit', 'Address'], rows);
}

function complianceMatrix(r: DesignResult): string {
  const c = r.counts; const n = (x: number | undefined) => x ?? 0;
  const spr = r.input.sprinklerType !== 'none';
  const rows: string[][] = [
    ['CBC Sect.907.2.9', 'R-2 corridor smoke detection', spr ? 'Fully-sprinklered exception applied' : `Corridor smoke at 30 ft o.c. (${n(c.smokeCorridor)})`, 'Addressed'],
    ['NFPA 72 Sect.10.6', 'Secondary power: 24 h standby + alarm', 'Battery calc incl. voice-amp + panel-class FACP draw — Section 8', 'Provided · EOR verifies'],
    ['NFPA 72 Sect.23.x', 'NAC end-of-line ≥ 16.0 V', 'Voltage-drop calc + per-circuit NAC schedule — Sections 9 / 9a', 'Provided · EOR verifies'],
    ['NFPA 72 Sect.23', 'SLC loop loading + device addressing', 'Loop loading & starting address schedule — Section 10a', 'Provided · EOR verifies'],
    ['NFPA 72 Sect.18.4', 'Audibility ≥ 15 dBA over ambient', 'Notification layout — Section 12', 'Provided · EOR verifies'],
    ['NFPA 72 Sect.18.4.5.3', 'Sleeping rooms ≥ 75 dBA + 520 Hz low-frequency', '520 Hz sounders in dwelling units', 'Addressed'],
    ['NFPA 72 Sect.18.5', 'Visible (strobe) spacing', 'Candela calc — Section 11', 'Provided · EOR verifies'],
    ['NFPA 72 Sect.26', 'Off-premises monitoring', 'Dual-path IP/cellular communicator', 'Provided'],
    ['NFPA 90A', 'Duct smoke detection (> 2000 CFM)', n(c.duct) > 0 ? `${n(c.duct)} duct kit(s) w/ HVAC shutdown` : 'As required by mechanical', 'Addressed'],
    ['CBC Sect.915', 'Carbon-monoxide detection', n(c.co) > 0 ? `${n(c.co)} CO detector(s)` : 'Per fuel-fired equipment', 'Addressed'],
    ['CBC Sect.3008 / NFPA 72 Sect.21.4', 'Elevator recall + shunt trip', n(c.elevatorRecall) > 0 ? `${n(c.elevatorRecall)} recall interface(s)` : 'As required', 'Addressed'],
    ['CBC Sect.1009.8', 'Area-of-refuge two-way communication', n(c.aor) > 0 ? `${n(c.aor)} station(s)` : 'Sprinklered exception / not required', 'Addressed'],
    ['CBC Sect.907.2.13', 'High-rise voice/EVACS + FCC', r.flags.isHighRise ? 'Voice/EVACS + firefighter command center' : 'Not high-rise — N/A', r.flags.isHighRise ? 'Addressed' : 'N/A'],
    ['NFPA 72 Sect.12.4 / CBC Sect.907.6.4', 'Circuit pathway survivability', (r.flags.isHighRise || r.flags.requiresVoiceEvac) ? '2-hr CI survivable cable on voice risers' : 'Standard pathways (Level 0)', (r.flags.isHighRise || r.flags.requiresVoiceEvac) ? 'Addressed' : 'N/A'],
    ['NFPA 72 Sect.21.5', 'Door holder release on alarm', n(c.doorHolder) > 0 ? `${n(c.doorHolder)} magnetic holder(s)` : 'As required', 'Addressed'],
    ['NFPA 70 Art. 760', 'FA circuit wiring methods & power-limited separation', 'FPLP/FPLR cable; separation from power', 'Provided'],
    ['ADA / CBC Ch.11', 'Accessible visible notification & reach ranges', 'Visible appliances throughout; pull stations at 48 in', 'Addressed'],
  ];
  return pageBreak + h2('2a. Code-Compliance Matrix')
    + para('How each governing requirement is satisfied by this design. "EOR verifies" = the engineer of record confirms the final layout/calc against the stamped drawings and the AHJ-adopted code edition.')
    + dataTable(['Code section', 'Requirement', 'How this design meets it', 'Status'], rows);
}

function powerSupplyCalc(r: DesignResult): string {
  const naDev = (r.counts.horn_strobe ?? 0) + (r.counts.strobe_only ?? 0) + (r.counts.speaker_strobe ?? 0);
  const slcDev = (r.counts.smokeCommon ?? 0) + (r.counts.smokeCorridor ?? 0) + (r.counts.heat ?? 0) + (r.counts.duct ?? 0) +
    (r.counts.co ?? 0) + (r.counts.pull ?? 0) + (r.counts.waterflow ?? 0) + (r.counts.tamper ?? 0) + (r.counts.relay ?? 0);
  return pageBreak + h2('10. Power-Supply Calculations')
    + para('NAC / SLC / auxiliary loading vs. supply capacity, with spare. The EOR finalizes against published draws for the selected equipment.')
    + dataTable(['Item', 'Value', 'Notes'], [
      ['SLC devices (addressable points)', String(slcDev), `vs. ${Math.ceil(slcDev / 159) || 1} loop(s) @ ~159 pts (panel-dependent)`],
      ['Notification appliances (NAC)', String(naDev), `across ${r.counts.nacBooster ?? 0} booster(s) + panel NACs`],
      ['NAC supply provided', `${r.counts.nacBooster ?? 0} × ~10 A booster(s) + FACP`, 'Size each NAC ≤80% per Sect.23'],
      ['Spare capacity target', '≥ 20%', 'NFPA 72 design margin'],
    ]);
}

function candelaCalc(r: DesignResult): string {
  return pageBreak + h2('11. Visible (Candela) Coverage Calculation')
    + para('Wall-mounted strobe candela by room size — NFPA 72 Table 18.5.5.5.1(a), single wall strobe centered on the longest wall:')
    + dataTable(['Max room dimension', 'Min candela (1 wall strobe)', 'Notes'], [
      ['20 ft', '15 cd', 'Small rooms / offices'],
      ['30 ft', '30 cd', ''],
      ['40 ft', '60 cd', ''],
      ['50 ft', '95 cd', ''],
      ['60 ft', '110 cd (135 cd ceiling)', 'Larger rooms add strobes or raise cd'],
    ])
    + para('Corridors (NFPA 72 Sect.18.5.5.5.2): ≤ 15 cd, one strobe per ≤ 15 ft of corridor and within 15 ft of each end; corridors > 100 ft require multiple synchronized strobes. Where two or more strobes are in any field of view they shall be synchronized (Sect.18.5.5.5.1). Mounting: wall strobes 80 in to 96 in AFF or 6 in below ceiling.')
    + para(`Basis of design for this project: 75 cd wall devices in corridors/common areas; sleeping rooms (per CBC accessible-unit count) use 177 cd per the spacing tables. Provided visible appliances: ${(r.counts.horn_strobe ?? 0) + (r.counts.strobe_only ?? 0) + (r.counts.speaker_strobe ?? 0)}. The EOR performs the final room-by-room calc against the architectural plans.`, true);
}

function audibleCalc(r: DesignResult): string {
  return pageBreak + h2('12. Audible Coverage Calculations')
    + para('Per NFPA 72 Sect.18.4: ≥15 dBA above average ambient, or ≥5 dBA above max 60-second sound, throughout. Sleeping areas require ≥75 dBA at the pillow and a 520 Hz low-frequency signal (Sect.18.4.5.3).' + (r.flags.requiresVoiceEvac ? ' Voice/EVACS must also meet intelligibility (STI-PA ≥ 0.45, Sect.18.4.10).' : ''))
    + dataTable(['Space', 'Target', 'Notes'], [
      ['Public / common areas', '≥15 dBA over ambient', 'Assumed 55 dBA ambient — verify'],
      ['Sleeping rooms', '≥75 dBA at pillow, 520 Hz', 'Low-frequency sounder required'],
      ['Mechanical spaces', '≥5 dBA over max', 'High-ambient — extra appliances'],
    ]);
}

function pathwaySurvivability(r: DesignResult): string {
  if (!(r.flags.isHighRise || r.flags.requiresVoiceEvac)) return '';
  return pageBreak + h2('13. Pathway Survivability')
    + para(`Voice/EVACS risers require Level 2/3 survivability (NFPA 72 Sect.12.4 / CBC Sect.907.6.4): 2-hour fire-rated (CI) cable or 2-hour rated enclosure. Estimated 2-hour CI cable: ${r.counts.ciCable_lf ?? 0} LF. Network/voice pathways routed for redundancy; Class A/X where required by the AHJ.`);
}

function networkDesign(r: DesignResult): string {
  if (!r.flags.isLargePanel && !r.flags.isHighRise) return '';
  return pageBreak + h2('14. Network Design')
    + para(`Networked addressable platform (${r.panel}). Node locations at the FACP and remote power-supply/annunciator points; fiber or copper backbone between nodes; Class A/B/X per the AHJ. The EOR finalizes node count, backbone media and survivability on the riser.`);
}

function interfaceList(r: DesignResult): string {
  const rows = [
    ['Elevator recall + shunt trip', (r.counts.elevatorRecall ?? 0) > 0 ? `${r.counts.elevatorRecall} interface` : 'As required', 'CBC Sect.3008 / NFPA 72 Sect.21.4 / ASME A17.1'],
    ['HVAC shutdown (duct smoke)', `${r.counts.duct ?? 0} unit(s)`, 'NFPA 90A'],
    ['Magnetic door holders', `${r.counts.doorHolder ?? 0}`, 'Release on alarm'],
    ['Fire/smoke doors', 'As shown', 'Coordinate Div 08'],
    ['Smoke control', r.flags.isHighRise ? 'Required' : 'Verify', 'CBC Sect.909 (if applicable)'],
    ['Generator monitoring', 'As shown', 'Run/fault supervisory'],
    ['BDA / DAS monitoring', 'As required', 'NFPA 1221 / 72 Sect.24.5'],
    ['Kitchen / clean-agent release', 'As required', 'Tie to suppression'],
  ];
  return pageBreak + h2('15. Interfaces & Emergency Control Functions')
    + para('Connected systems the fire alarm monitors or controls (details by the EOR):')
    + dataTable(['Interface', 'This project', 'Code basis'], rows);
}

function monitoringReqs(): string {
  return pageBreak + h2('16. Monitoring Requirements')
    + para('The system reports to a UL-listed supervising (central) station via a dual-path IP/cellular communicator (NFPA 72 Sect.26). Provide the supervising-station name, account, and DACT/IP communicator details on the riser. Year-1 monitoring is included in the ROM; renewal at owner option.');
}

function productData(): string {
  return pageBreak + h2('17–18. Product Data Sheets & Listings')
    + para('The submittal includes manufacturer cut sheets for the FACP, detectors, notification appliances, modules, power supplies and network equipment, each showing the UL listing (UL 864 / 268 / 521 / 464 / 1971 / 1481 / 2017 / 2572) and FM approval where required, and confirming compatibility with the selected panel. Cut sheets are attached to the stamped submittal (not reproduced here).');
}

function specifications(): string {
  return pageBreak + h2('19. Specifications (Division 28 21 00 / 28 31 00)')
    + bullets([
      'Products: addressable FACP, detectors, appliances, modules, power supplies, survivable cable — all UL-listed & panel-compatible.',
      'Installation: per NFPA 72 / 70, manufacturer instructions, AHJ amendments; FPLP/FPLR cable; conduit where required.',
      'Testing: 100% point-to-point pre-test + AHJ-witnessed acceptance test per NFPA 72 Ch.14.',
      'Documentation: as-builts, record drawings, O&M manual, completion & ITM forms.',
      'Warranty: 1-year parts & labor; year-1 monitoring included.',
    ]);
}

function deferredItems(): string {
  return pageBreak + h2('21. Deferred Submittal Items (if the AHJ allows)')
    + para('Where permitted by the AHJ, the following may be deferred to shop-drawing stage: final programming files, device addresses, manufacturer shop drawings, and final calculations. Many AHJs require these at permit — confirm before relying on deferral.');
}

function contractorDeliverables(): string {
  return pageBreak + h2('Appendix — What 3DTSI provides the Engineer of Record')
    + para('To produce the stamped package efficiently, 3DTSI supplies the EOR:')
    + bullets([
      'Architectural plans (PDF), reflected ceiling plans, life-safety plans',
      'Code summary sheets and any existing fire-alarm drawings',
      'Sprinkler, HVAC and elevator drawings',
      'Fire-alarm specifications and the sequence-of-operations requirements',
      'AHJ requirements / adopted code edition + amendments',
      'Preferred manufacturer (selected on the cover) and any owner standards',
      'Scope narrative, device counts and ceiling heights',
      'Monitoring requirements',
    ]);
}

// ── Public entry point — Word (.doc) ─────────────────────────────────────────
export function buildFaSubmittalDoc(r: DesignResult, today: string): Blob {
  const project = r.input.projectName || '__________________________';
  const occ = r.input.occupancy === 'r2_condos' ? 'R-2 Residential (condominiums)' : 'R-2 Residential (apartments)';
  const titlePage = `
    <div class="titlepage">
      <div class="tp-brand">3D&nbsp;TECHNOLOGY&nbsp;SERVICES,&nbsp;INC.</div>
      <div class="tp-rule"></div>
      <div class="tp-title">Fire Alarm System</div>
      <div class="tp-sub">Design-Build Submittal Package</div>
      <div class="tp-tag">Basis of Design &mdash; for Professional Engineer Review &amp; Stamp</div>
      <table class="tp-info">
        <tr><td class="tpk">Project</td><td>${esc(project)}</td></tr>
        <tr><td class="tpk">Location / AHJ</td><td>${esc(r.input.cityAhj || '—')}</td></tr>
        <tr><td class="tpk">Occupancy</td><td>${esc(occ)}</td></tr>
        <tr><td class="tpk">Prepared by</td><td>3D Technology Services, Inc. &middot; 3dtsi.com</td></tr>
        <tr><td class="tpk">Date</td><td>${esc(today)}</td></tr>
      </table>
      <div class="tp-note">This package is the design-build basis of design and scope. It is <b>NOT</b> a stamped engineering document &mdash; the licensed Fire Protection Engineer / NICET&nbsp;Level&nbsp;III designer of record verifies all device counts, spacing, voltage drop and battery sizing and applies the seal before submittal to the AHJ.</div>
    </div>`;
  const headerFooter = `
    <div style="mso-element:header" id="h1"><p class="MsoHeader">
      <table class="hdr"><tr><td class="hl">3D Technology Services, Inc.</td>
      <td class="hr">Fire Alarm Submittal &middot; 3dtsi.com</td></tr></table></p></div>
    <div style="mso-element:header" id="fh1"><p class="MsoHeader">&nbsp;</p></div>
    <div style="mso-element:footer" id="f1"><p class="MsoFooter">
      Basis of Design &mdash; for PE Review &amp; Stamp &nbsp;|&nbsp; ${esc(project)} &nbsp;|&nbsp;
      Page <span style='mso-field-code:" PAGE "'></span> of <span style='mso-field-code:" NUMPAGES "'></span></p></div>
    <div style="mso-element:footer" id="ff1"><p class="MsoFooter">&nbsp;</p></div>`;
  const body = `
    ${titlePage}
    <div class="Section1">
    ${coverSheet(r)}
    ${sheetIndex(r)}
    ${codeAnalysis(r)}
    ${complianceMatrix(r)}
    ${basisOfDesign(r)}
    ${drawingDeliverables(r)}
    ${riserDiagram()}
    ${sequenceMatrix(r)}
    ${deviceLegend()}
    ${equipmentSchedule(r)}
    ${deviceSchedule(r)}
    ${batteryCalc(r)}
    ${voltageDrop(r)}
    ${nacCircuitSchedule(r)}
    ${powerSupplyCalc(r)}
    ${slcLoopSchedule(r)}
    ${candelaCalc(r)}
    ${audibleCalc(r)}
    ${pathwaySurvivability(r)}
    ${networkDesign(r)}
    ${interfaceList(r)}
    ${monitoringReqs()}
    ${productData()}
    ${specifications()}
    ${bomSection(r)}
    ${deferredItems()}
    ${scopeLetter(r)}
    ${rfiChecklist(r)}
    ${stampBlock()}
    ${contractorDeliverables()}
    </div>
    ${headerFooter}`;

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>Fire Alarm Submittal — ${esc(project)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>
  /* Running header/footer on every page after the title page (Word). */
  @page Section1 {
    size: 8.5in 11in; margin: 1.05in 0.7in 0.85in 0.7in;
    mso-header-margin: 0.45in; mso-footer-margin: 0.4in;
    mso-header: h1; mso-footer: f1; mso-first-header: fh1; mso-first-footer: ff1; mso-title-page: yes;
  }
  div.Section1 { page: Section1; }
  body { font-family: Calibri, Arial, sans-serif; font-size: 10.5pt; color: #111; }
  h2 { color: #0A5C64; font-size: 13pt; border-bottom: 1.5pt solid #C9A227; padding-bottom: 2pt; margin: 14pt 0 6pt; }
  p { margin: 5pt 0; line-height: 1.35; }
  .mut { color: #7A8585; font-size: 9pt; }

  /* Title page */
  .titlepage { page-break-after: always; text-align: center; }
  .tp-brand { color: #0A5C64; font-weight: bold; font-size: 16pt; letter-spacing: 1pt; margin-top: 150pt; }
  .tp-rule { border-top: 2.5pt solid #C9A227; width: 55%; margin: 10pt auto 0; }
  .tp-title { font-size: 34pt; font-weight: bold; color: #111; margin-top: 26pt; }
  .tp-sub { font-size: 17pt; color: #0E7C86; margin-top: 4pt; }
  .tp-tag { font-size: 11pt; color: #7A8585; margin-top: 14pt; font-style: italic; }
  table.tp-info { width: 62%; margin: 40pt auto 0; font-size: 11pt; text-align: left; }
  table.tp-info td { padding: 4pt 8pt; border-bottom: 0.5pt solid #E2E2E2; }
  table.tp-info .tpk { font-weight: bold; color: #0A5C64; width: 38%; }
  .tp-note { width: 75%; margin: 40pt auto 0; font-size: 9pt; color: #7A8585; border-top: 0.5pt solid #C9A227; padding-top: 10pt; }

  /* Word running header band + footer */
  p.MsoHeader, p.MsoFooter { margin: 0; border: none; }
  table.hdr { width: 100%; border-collapse: collapse; background: #0E7C86; border-bottom: 2.5pt solid #C9A227; }
  table.hdr td { padding: 4pt 8pt; color: #fff; font-size: 9pt; }
  table.hdr .hl { font-weight: bold; }
  table.hdr .hr { text-align: right; }
  p.MsoFooter { color: #7A8585; font-size: 8pt; text-align: center; border-top: 0.5pt solid #E2E2E2; padding-top: 3pt; }

  .caution { border: 1.5pt solid #D93535; background: #FFF3D6; padding: 8pt 10pt; margin: 10pt 0; }
  .caution-h { color: #D93535; font-weight: bold; font-size: 12pt; margin-bottom: 4pt; }
  table { border-collapse: collapse; width: 100%; margin: 6pt 0; font-size: 9.5pt; }
  table.kv td { padding: 2.5pt 6pt; vertical-align: top; }
  table.kv .k { font-weight: bold; width: 38%; }
  table.data th { background: #0A5C64; color: #fff; text-align: left; padding: 4pt 6pt; font-size: 9pt; }
  table.data th.num { text-align: right; }
  table.data td { padding: 3pt 6pt; border-bottom: 0.5pt solid #E2E2E2; }
  table.data td.num { text-align: right; }
  table.data tr.alt td { background: #F2F6F6; }
  table.total { background: #EDEFEF; margin-top: 8pt; }
  table.total td { padding: 8pt 10pt; }
  table.total .big { text-align: right; color: #0A5C64; font-weight: bold; font-size: 16pt; }
  table.stamp td { border: 1pt solid #111; height: 230pt; vertical-align: top; padding: 10pt; }
  ul, ol { margin: 4pt 0 8pt 0; }
  li { margin: 2pt 0; line-height: 1.3; }
</style></head>
<body>${body}</body></html>`;

  return new Blob(['﻿' + html], { type: 'application/msword' });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}
